import { useRef, useEffect, useState, useCallback } from 'react';

/**
 * Finds the actual scrollable element inside a container.
 * For Ant Design Tables the real scroll container is .ant-table-body or
 * .ant-table-content — not the wrapper div.
 * Falls back to the wrapper itself for plain HTML tables.
 */
function findScrollable(container) {
  if (!container) return null;

  // Ant Design Table scroll containers (in order of specificity)
  const antSelectors = [
    '.ant-table-body',        // Table with scroll={{ y }}
    '.ant-table-content',     // Table without fixed height
    '.ant-table-wrapper',
  ];

  for (const sel of antSelectors) {
    const el = container.querySelector(sel);
    if (el && el.scrollWidth > el.clientWidth) return el;
  }

  // For elements that are directly scrollable (plain HTML tables wrapped in overflow-x div)
  if (container.scrollWidth > container.clientWidth) return container;

  // Walk direct children looking for a scrollable one
  for (const child of Array.from(container.children)) {
    if (child.scrollWidth > child.clientWidth) return child;
  }

  // Default: the container itself — overflow will be detected once data loads
  return container;
}

export default function useScrollSync() {
  const wrapperRef = useRef(null);   // the .scroll-sync-content div
  const topRef = useRef(null);       // the .scroll-sync-track div
  const [overflow, setOverflow] = useState({ hasOverflow: false, scrollWidth: 0 });
  const scrollableRef = useRef(null);
  const ticking = useRef(false);

  // Detect overflow and find the real scrollable child
  useEffect(() => {
    const container = wrapperRef.current;
    if (!container) return;

    const check = () => {
      // Re-find the scrollable element every check (table content may render later)
      const scrollable = findScrollable(container);
      scrollableRef.current = scrollable;

      const el = scrollable || container;
      const hasOverflow = el.scrollWidth > el.clientWidth;
      setOverflow({
        hasOverflow,
        scrollWidth: el.scrollWidth,
      });
    };

    // Initial check
    check();

    // Watch for size changes on the container
    const ro = new ResizeObserver(check);
    ro.observe(container);

    // Also watch for DOM changes inside (Ant Design renders table body asynchronously)
    const mo = new MutationObserver(check);
    mo.observe(container, { childList: true, subtree: true, attributes: false });

    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  // Sync scroll from top bar → content
  const handleTopScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;

    const top = topRef.current;
    const scrollable = scrollableRef.current || wrapperRef.current;
    if (!top || !scrollable) {
      ticking.current = false;
      return;
    }

    const maxSrc = Math.max(1, top.scrollWidth - top.clientWidth);
    const maxTgt = Math.max(0, scrollable.scrollWidth - scrollable.clientWidth);
    scrollable.scrollLeft = (top.scrollLeft / maxSrc) * maxTgt;

    requestAnimationFrame(() => { ticking.current = false; });
  }, []);

  // Sync scroll from content → top bar
  const handleBottomScroll = useCallback((e) => {
    if (ticking.current) return;
    ticking.current = true;

    const top = topRef.current;
    const scrollable = e?.target || scrollableRef.current || wrapperRef.current;
    if (!top || !scrollable) {
      ticking.current = false;
      return;
    }

    const maxSrc = Math.max(1, scrollable.scrollWidth - scrollable.clientWidth);
    const maxTgt = Math.max(0, top.scrollWidth - top.clientWidth);
    top.scrollLeft = (scrollable.scrollLeft / maxSrc) * maxTgt;

    requestAnimationFrame(() => { ticking.current = false; });
  }, []);

  // Attach scroll listener to the actual scrollable child (Ant Design internal div)
  useEffect(() => {
    const container = wrapperRef.current;
    if (!container) return;

    let attached = null;

    const attach = () => {
      const scrollable = findScrollable(container);
      if (scrollable && scrollable !== container && scrollable !== attached) {
        if (attached) attached.removeEventListener('scroll', handleBottomScroll);
        scrollable.addEventListener('scroll', handleBottomScroll, { passive: true });
        attached = scrollable;
        scrollableRef.current = scrollable;
      }
    };

    attach();

    // Retry after DOM settles (Ant Design lazy-renders the table body)
    const timer = setTimeout(attach, 300);

    return () => {
      clearTimeout(timer);
      if (attached) attached.removeEventListener('scroll', handleBottomScroll);
    };
  }, [handleBottomScroll]);

  return { topRef, bottomRef: wrapperRef, overflow, handleTopScroll, handleBottomScroll };
}
